import { Box, Divider, Grid, IconButton, MenuItem, Select, styled } from '@mui/material';
import { Level } from '@tiptap/extension-heading';
import { EditorContent } from '@tiptap/react';
import { useEffect, useState } from 'react';
import { CgRedo, CgUndo } from 'react-icons/cg';
import { IComposableAnalyticsCharts, IComposableDashboardContext } from 'wisestep-api';
import { WsButton, WsColors, WsIconDelete, WsIconEdit } from 'wisestep-base';

import EmailFooter from '../../../.../../components/BulkEmail/WsEditor/components/EmailFooter';
import {
  BoldIcon,
  CenterAlignIcon,
  ColorHighlightIcon,
  ColorTextIcon,
  ItalicIcon,
  LeftAlignIcon,
  RightAlignIcon,
  StyledBox,
  useWsEditor,
} from '../../../components/BulkEmail/WsEditor/WsEditor';
import { useComposableDashboardsContext } from '../../../features/analyticsPage/hooks/useComposableDashboardsContext';

export interface IFreeTextEditorProps {
  text?: string;
  id: string;
  dashboardEditMode: boolean;
}

const CustomEditorContent = styled(EditorContent)(({ theme }) => ({
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
  '.ProseMirror:focus': {
    border: 'none',
    outline: 'none',
  },
}));

const FreeTextEditor = ({ text, id: widgetId, dashboardEditMode }: IFreeTextEditorProps) => {
  const { setSelectedDashboard, selectedDashboard } =
    useComposableDashboardsContext() as Required<IComposableDashboardContext>;
  const [interactionState, setInteractionState] = useState<'view' | 'edit'>('edit');
  const [content, setContent] = useState(text);
  useEffect(() => (text ? setInteractionState('view') : setInteractionState('edit')), [text]);

  const onChange = (text: string | null) => {
    setContent(text ?? '');
  };

  const editor = useWsEditor(
    content ?? '<h1></h1>',
    onChange,
    () => null,
    () => null,
    () => null,
    'Type heading and description here for your dashboard...'
  );

  const inputStyles = {
    width: '20',
    marginLeft: '-1',
    margginTop: '5',
    marginBottom: '-5',
  };

  const textFieldStyle = (
    display: string,
    marginLeft: string,
    marginBottom: string,
    marginTop: string,
    width: string,
    height: string,
    opacity: string
  ) => {
    return {
      display: display,
      marginLeft: marginLeft,
      marginBottom: marginBottom,
      marginTop: marginTop,
      width: width,
      height: height,
      opacity: opacity,
    };
  };

  const onRemoveWidget = () => {
    setSelectedDashboard((prevSelectedDashboard) => {
      const prevCharts = prevSelectedDashboard.charts as IComposableAnalyticsCharts[];
      const widgetIndex = prevCharts.findIndex((chart) => chart.id == widgetId);
      prevCharts.splice(widgetIndex, 1);
      return { ...prevSelectedDashboard };
    });
  };

  const onSave = () => {
    const charts = selectedDashboard.charts as IComposableAnalyticsCharts[];
    const currentWidget = charts.find((chart) => chart.id == widgetId);
    currentWidget && (currentWidget.text = content);
    setInteractionState('view');
  };

  return (
    <>
      {dashboardEditMode && (
        <IconButton
          onClick={() => setInteractionState('edit')}
          sx={{
            position: 'absolute',
            top: '0.4rem',
            right: '1.7rem',
            zIndex: 1,
            color: WsColors.TertiaryShades300,
          }}
        >
          <WsIconEdit />
        </IconButton>
      )}

      {interactionState === 'edit' ? (
        <Grid
          item
          xs={12}
          sx={{ height: 'calc(100% - 55px)', width: '100%', overflow: 'auto' }}
          onClick={() => editor?.chain().focus()}
          onKeyDown={() => editor?.chain().focus()}
        >
          <CustomEditorContent editor={editor} />
        </Grid>
      ) : (
        <Grid
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            margin: 'auto',
            height: '100%',
            ml: 4,
          }}
          height="100%"
          dangerouslySetInnerHTML={{ __html: content || '' }}
        />
      )}

      {interactionState === 'edit' && (
        <EmailFooter>
          <Grid
            xs={12}
            item
            container
            p={1}
            py={2}
            display="flex"
            sx={{ borderTop: `1 solid ${WsColors.TertiaryShades100}` }}
          >
            <Grid xs={11} item container columnGap={0.5}>
              <StyledBox>
                <CgUndo onClick={() => editor?.chain().focus().undo().run()} size={25} />
              </StyledBox>
              <StyledBox>
                <CgRedo onClick={() => editor?.chain().focus().redo().run()} size={25} />
              </StyledBox>
              <Divider orientation="vertical" flexItem />
              <StyledBox>
                <BoldIcon onClick={() => editor?.chain().focus().toggleBold().run()} />
              </StyledBox>
              <StyledBox>
                <ItalicIcon onClick={() => editor?.chain().focus().toggleItalic().run()} />
              </StyledBox>
              <StyledBox>
                <LeftAlignIcon onClick={() => editor?.chain().focus().setTextAlign('left').run()} />
              </StyledBox>

              <StyledBox>
                <CenterAlignIcon
                  onClick={() => editor?.chain().focus().setTextAlign('center').run()}
                />
              </StyledBox>
              <StyledBox>
                <RightAlignIcon
                  onClick={() => editor?.chain().focus().setTextAlign('right').run()}
                />
              </StyledBox>

              <Divider orientation="vertical" flexItem />
              <Select
                defaultValue={1}
                size="small"
                onChange={(ev) =>
                  ev.target.value !== 0
                    ? editor
                        ?.chain()
                        .focus()
                        .toggleHeading({ level: ev.target.value as Level })
                        .run()
                    : editor?.chain().focus().setParagraph().run()
                }
              >
                <MenuItem value={0}>T</MenuItem>
                <MenuItem value={1}>Heading 1</MenuItem>
                <MenuItem value={2}>Heading 2</MenuItem>
                <MenuItem value={3}>Heading 3</MenuItem>
                <MenuItem value={4}>Heading 4</MenuItem>
                <MenuItem value={5}>Heading 5</MenuItem>
                <MenuItem value={6}>Heading 6</MenuItem>
              </Select>
              <Divider orientation="vertical" flexItem />
              <StyledBox>
                <Box pl={0.5}>
                  <label htmlFor="colorPicker" title="Text color">
                    <i>
                      <ColorTextIcon style={inputStyles} />
                    </i>
                  </label>
                  <input
                    type="color"
                    id="colorPicker"
                    style={textFieldStyle('block', '10', '10', '-10', '0', '0', '0')}
                    onInput={(event) =>
                      editor
                        ?.chain()
                        .focus()
                        .setColor((event.target as HTMLInputElement).value)
                        .run()
                    }
                  />
                </Box>
              </StyledBox>
              <StyledBox>
                <Box pl={0.5}>
                  <label htmlFor="highlightColor" className="color-icon" title="Highlight color">
                    <i>
                      <ColorHighlightIcon style={inputStyles} />
                    </i>
                  </label>
                  <input
                    type="color"
                    id="highlightColor"
                    style={textFieldStyle('block', '10', '10', '-10', '0', '0', '0')}
                    onInput={(event) =>
                      editor
                        ?.chain()
                        .focus()
                        .toggleHighlight({ color: (event.target as HTMLInputElement).value })
                        .run()
                    }
                  />
                </Box>
              </StyledBox>
            </Grid>
            <Grid
              xs={1}
              item
              display="flex"
              justifyContent="flex-end"
              flexDirection="row"
              columnGap={1}
            >
              <WsButton variant="outlined" onClick={onRemoveWidget}>
                <WsIconDelete color={WsColors.Red} />
              </WsButton>
              <WsButton onClick={onSave}>Save</WsButton>
            </Grid>
          </Grid>
        </EmailFooter>
      )}
    </>
  );
};
export default FreeTextEditor;
